// React and hooks

// Utilities and API
import { verifyLogin } from 'util/cache'
import { useDocTitle } from 'util/hooks'
import { useGetLessonAssignedClassQuery, useGetLessonQuery, useHasResultsQuery, useGetClassQuery, useRenameLessonMutation, useUpdateLessonMetaMutation, useDeleteQuestionMutation } from 'util/rtk-api'

// Types

// Components
import { lessonReducer, buildSlidesArray } from 'lesson-builder/lesson-reducer'
import { useLessonPersistence } from 'lesson-builder/use-lesson-persistence'
import { useLessonBuilderOverlays } from 'lesson-builder/use-lesson-builder-overlays'
import { LessonBuilderOverlays } from 'lesson-builder/lesson-builder-overlays'
import { LessonBuilderHeader } from 'lesson-builder/components/lesson-builder-header'
import { PresentationSlide } from 'lesson-builder/presentation-slide'
import { QuestionSlide } from 'lesson-builder/question-slide'
import { Sidebar } from 'lesson-builder/sidebar'
import { SlideNav } from 'lesson-builder/slide-nav'

// Context
import { LessonProvider } from 'lesson-builder/context'

// Assets
import sideBarIcon from 'img/sidebar.svg'
import React, { useState, useEffect, useCallback, useReducer, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
// Styles
import 'css/pages/lesson-builder.css'

const HideSideBarTooltip: React.FC = () => {
  return (
    <div className="hide-sidebar-tooltip">
      Hide sidebar
    </div>
  )
}

export const LessonBuilder: React.FC = () => {
  const searchParams = new URLSearchParams(window.location.search)
  const lessonId = searchParams.get('lessonId')
  const [showHideSidebarTooltip, setShowHideSidebarTooltip] = useState(false)
  const overlays = useLessonBuilderOverlays()
  const [isClassAssigned, setIsClassAssigned] = useState(false)
  const [assignedClass, setAssignedClass] = useState<{ classId: string; name: string } | null>(null)
  const { data: assignedClassData } = useGetLessonAssignedClassQuery({ lessonId })
  const { data: hasResultsData } = useHasResultsQuery({ lessonId: lessonId! }, { skip: !lessonId })
  const { data: classData } = useGetClassQuery({ classId: assignedClass?.classId! }, { skip: !assignedClass?.classId })
  const [renameLesson] = useRenameLessonMutation()
  const [updateLessonMeta] = useUpdateLessonMetaMutation()
  const [deleteQuestionMutation] = useDeleteQuestionMutation()
  const { data: lessonData } = useGetLessonQuery({ lessonId: lessonId! }, { skip: !lessonId })
  const [isRenaming, setIsRenaming] = useState(false)
  const [renamingValue, setRenamingValue] = useState('')

  const [lessonState, baseDispatch] = useReducer(lessonReducer, {
    name: '',
    lessonPdf: null,
    videoLinksByPage: {},
    numSlides: 0,
    questionSlides: [],
    totalUnapprovedQuestions: 0,
    currentSlide: 0,
    hideSidebar: false,
    status: '',
    lastEditTimestamp: null
  })

  const { dispatch } = useLessonPersistence({
    lessonId,
    lessonData,
    assignedClassData,
    lessonState,
    baseDispatch,
    setAssignedClass,
    setIsClassAssigned,
    setShowReadyPopup: overlays.setShowReadyPopup,
  })

  const {
    name,
    lessonPdf,
    videoLinksByPage,
    currentSlide,
    hideSidebar
  } = lessonState

  // Build slides array for rendering (computed from questionSlides)
  const slides = buildSlidesArray(lessonState.questionSlides, lessonState.numSlides)

  const allApproved = lessonState.totalUnapprovedQuestions === 0

 const handleQuestionApproval = useCallback((questionIndex: number, difficulty: string, approved: boolean) => {
    dispatch({
      type: approved ? 'APPROVE_QUESTION' : 'DISAPPROVE_QUESTION',
      slideIndex: currentSlide,
      questionIndex,
      difficulty
    })
  }, [currentSlide])

  const handleApproveAll = useCallback(() => {
    dispatch({ type: 'APPROVE_ALL_QUESTIONS', slideIndex: currentSlide })
  }, [currentSlide])

  const canDeleteQuestions = lessonState.status === 'Draft' && !hasResultsData?.hasResults

  const handleDeleteQuestion = useCallback((questionIndex: number, difficulty: string, questionId: string) => {
    // Optimistically update local state
    dispatch({
      type: 'DELETE_QUESTION',
      slideIndex: currentSlide,
      questionIndex,
      difficulty,
    })
    // Delete from backend (EduQuery + DynamoDB)
    if (lessonId) {
      deleteQuestionMutation({
        lessonId,
        questionId,
        difficulty,
        slideIndex: currentSlide,
        questionIndex,
      })
    }
  }, [currentSlide, lessonId, deleteQuestionMutation])

  // Add actions for slide navigation and sidebar
  const handleSlideChange = useCallback((slideNum: number) => {
    dispatch({
      type: 'SET_CURRENT_SLIDE',
      slideNum
    })
  }, [])

  const handleToggleSidebar = useCallback(() => {
    dispatch({
      type: 'TOGGLE_SIDEBAR'
    })
  }, [])

  const handleLaunchLesson = () => {
    overlays.setShowLaunchOverlay(true);
  };


  const navigate = useNavigate()

  useEffect(() => {
    verifyLogin(navigate)
  }, [lessonData, assignedClassData])

  const handleStartRename = () => {
    setRenamingValue(name)
    setIsRenaming(true)
  }

  const handleConfirmRename = async () => {
    if (renamingValue.trim() && lessonId) {
      try {
        await renameLesson({ lessonId, name: renamingValue.trim() })
        dispatch({ type: 'INIT_LESSON', payload: { ...lessonData, name: renamingValue.trim() } })
      } catch (error) {
        console.error('Error renaming lesson:', error)
      }
    }
    setIsRenaming(false)
  }

  const handleCancelRename = () => {
    setIsRenaming(false)
    setRenamingValue('')
  }

  useDocTitle(lessonState.name || 'All for One - Lesson Builder')

  return (
    <LessonProvider lessonId={lessonId}>
      <div className="lesson-builder">
        <LessonBuilderOverlays
          overlays={overlays}
          lessonId={lessonId}
          lessonData={lessonData}
          classData={classData}
          assignedClass={assignedClass}
          name={name}
          navigate={navigate}
          onLaunchLesson={handleLaunchLesson}
          setIsClassAssigned={setIsClassAssigned}
          setAssignedClass={setAssignedClass}
        />

        {/* Her `.top-bar-page`: the header's band is 900px where the
            workspace below is 1120px. The band is the page's layout, not the
            header's -- the header stays a content brick that renders the same
            three blocks wherever it is placed. */}
        <div className="header-band">
          <LessonBuilderHeader
            allApproved={allApproved}
            unapprovedCount={lessonState.totalUnapprovedQuestions}
            teachStatus={lessonState.status}
            lessonName={name}
            setShowAssignClassOverlay={overlays.setShowAssignClassOverlay}
            isClassAssigned={isClassAssigned}
            assignedClassName={assignedClass?.name}
            gradeBand={lessonData?.gradeBand}
            subject={lessonData?.subject}
            onUpdateMeta={(patch) => { if (lessonId) updateLessonMeta({ lessonId, ...patch }) }}
            hasResults={hasResultsData?.hasResults}
            onLaunchLesson={handleLaunchLesson}
            onShowStudentLogins={() => overlays.setShowStudentLoginsOverlay(true)}
            onCopyToClass={() => overlays.setCopyOverlayMode('copy')}
            onReassign={() => overlays.setCopyOverlayMode('reassign')}
            onPrintDraft={() => overlays.setShowPrintDraftOverlay(true)}
            onPrintAnswerKey={() => overlays.setShowPrintAnswerKeyOverlay(true)}
            onPrintWorksheets={() => overlays.setShowPrintWorksheetsOverlay(true)}
            isRenaming={isRenaming}
            renamingValue={renamingValue}
            onStartRename={handleStartRename}
            onRenamingValueChange={setRenamingValue}
            onConfirmRename={handleConfirmRename}
            onCancelRename={handleCancelRename}
          />
        </div>
        <div
          className={'workspace ' + (hideSidebar ? 'hide-sidebar' : '')}
        >
          <div className="sidebar-container">
            <Sidebar
              lessonPdf={lessonPdf}
              slides={slides}
              currentSlide={currentSlide}
              hide={hideSidebar}
              onSlideClick={handleSlideChange}
            />
            <div
              className="toggle-sidebar"
              onClick={handleToggleSidebar}
              onMouseOver={() => setShowHideSidebarTooltip(true)}
              onMouseOut={() => setShowHideSidebarTooltip(false)}
            >
              <img src={sideBarIcon} alt="toggle sidebar" />
              {showHideSidebarTooltip && <HideSideBarTooltip />}
            </div>
          </div>
          <div className="slide-column">
            <div className="slide-area">
              {slides[currentSlide]?.type === 'presentation'
                ? (<PresentationSlide
                  sizing="fill"
                  lessonPdf={lessonPdf}
                  pageNumber={slides[currentSlide].index! + 1}
                  isCurrent={currentSlide === slides[currentSlide].index}
                  videoLinks={videoLinksByPage[slides[currentSlide].index! + 1] ?? []}
                />)
                : <QuestionSlide
                  slideIndex={currentSlide}
                  slideQuestions={slides[currentSlide]?.questionsByDifficulty}
                  learningStage={slides[currentSlide]?.learningStage}
                  onApproveQuestion={handleQuestionApproval}
                  onApproveAll={handleApproveAll}
                  dispatch={dispatch}
                  canDelete={canDeleteQuestions}
                  onDeleteQuestion={handleDeleteQuestion}
                  canEdit={!hasResultsData?.hasResults}
                />
              }
            </div>
            <SlideNav
              slides={slides}
              currentSlide={currentSlide}
              onChange={handleSlideChange}
              onToggleSidebar={handleToggleSidebar}
            />
          </div>
        </div>
      </div>
    </LessonProvider>
  )
}
